import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { authApi } from '../services/api/endpoints/auth';
import type { User, AuthBusinessUnit, AuthState } from '../types/auth';
import type { Organization } from '../types/organization';
import { useAuditorStore } from './auditorStore';
import { isAuditRole } from '../utils/roles';

/**
 * Interface complète du store d'authentification
 */
interface AuthStore extends AuthState {
  // Actions
  initialize: () => Promise<void>;
  logout: () => Promise<void>;
  loginWithSSO: () => Promise<boolean>;
  
  // Setters
  setUser: (user: User | null) => void;
  setOrganization: (organization: Organization | null) => void;
  setBusinessUnit: (businessUnit: AuthBusinessUnit | null) => void;
  
  // Getters utilitaires
  hasCompanyAccess: () => boolean;
  getUserScope: () => 'company' | 'unit' | null;
  canAccessBusinessUnit: (businessUnitId: string) => boolean;
}/**
 * Store Zustand pour la gestion de l'authentification et du contexte global
 * 
 * Ce store est la source de vérité pour :
 * - L'utilisateur connecté
 * - Son organisation (company)
 * - Son unité d'affaires (businessUnit) avec le scope
 * 
 * Au démarrage de l'app, appeler authStore.initialize() qui :
 * 1. Appelle GET /auth/me
 * 2. Récupère user + organization + businessUnit
 * 3. Stocke ces données dans le store
 * 4. Les autres stores (businessUnitStore) peuvent s'y abonner
 */
export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      // État initial
      user: null,
      organization: null,
      businessUnit: null,
      isAuthenticated: false,
      loading: true,
      syncStatus: 'idle',
      auditorProfile: undefined,
      
      /**
       * Initialise le contexte d'authentification via /auth/me
       * À appeler au démarrage de l'application après authentification Auth0
       * 
       * Note: Le token est géré par Auth0 via getAccessTokenSilently() dans ApiService
       * On ne vérifie plus localStorage car Auth0 gère le token différemment
       */
      initialize: async () => {
        set({ loading: true });
        
        try {
          const response = await authApi.getMe();
          
          if (response.success && response.data) {
            const { user, organization, businessUnit, syncStatus, message, auditorProfile } = response.data;
            
            // --- Cas spécial : utilisateur auditeur ---
            // Le backend renvoie un auditorProfile au lieu d'organization/businessUnit
            if (isAuditRole(user.role)) {
              useAuditorStore.getState().setAuditorProfile(auditorProfile ?? null);
              // Préserver l'organisation de l'entreprise active si l'auditeur en a sélectionné une
              const currentActiveCompany = useAuditorStore.getState().activeCompany;
              set({
                user,
                organization: currentActiveCompany?.organization ?? null,
                businessUnit: null,
                isAuthenticated: true,
                loading: false,
                syncStatus: 'synced',
                auditorProfile: auditorProfile ?? undefined,
              });
              console.log('✅ Contexte auditeur initialisé:', {
                userId: user.id,
                totalCompanies: auditorProfile?.totalCompanies ?? 0,
              });
              return;
            }

            // Vérifier si l'organisation est en cours de synchronisation
            if (syncStatus === 'pending') {
              console.warn('⏳ Organisation en cours de synchronisation:', message);
              set({
                user,
                organization: null,
                businessUnit: null,
                isAuthenticated: true,
                loading: false,
                syncStatus: 'pending'
              });
              return;
            }
            
            set({
              user,
              organization,
              businessUnit,
              isAuthenticated: true,
              loading: false,
              syncStatus: syncStatus || 'synced'
            });
            
            console.log('✅ Contexte auth initialisé:', {
              userId: user.id,
              role: user.role,
              organizationId: organization?.id,
              businessUnitId: businessUnit?.id,
              scope: businessUnit?.scope
            });
          } else {
            // Fallback vers /auth/verify si /auth/me échoue
            console.warn('⚠️ /auth/me a échoué ou données incomplètes, fallback vers /auth/verify');
            const verifyResponse = await authApi.verifyToken();
            
            if (verifyResponse.success && verifyResponse.data) {
              set({
                user: verifyResponse.data.user,
                organization: null,
                businessUnit: null,
                isAuthenticated: true,
                loading: false,
                syncStatus: 'pending'
              });
            } else {
              // En mode développement, utiliser les données du FallbackService
              console.warn('⚠️ Utilisation des données de démonstration');
              set({
                user: null,
                organization: null,
                businessUnit: null,
                isAuthenticated: true, // Garder authentifié si Auth0 a validé
                loading: false,
                syncStatus: 'error'
              });
            }
          }
        } catch (error) {
          console.error('❌ Erreur initialisation auth:', error);
          // Ne pas supprimer le token Auth0 - il est géré par Auth0
          // Garder l'utilisateur authentifié mais sans organisation
          set({
            user: null,
            organization: null,
            businessUnit: null,
            isAuthenticated: true, // Auth0 a validé, garder authentifié
            loading: false,
            syncStatus: 'error'
          });
        }
      },
      
      /**
       * Connexion via SSO
       */
      loginWithSSO: async () => {
        try {
          const response = await authApi.loginWithSSO();
          
          if (response.success && response.data) {
            localStorage.setItem('token', response.data.token);
            
            // Initialiser le contexte complet après login
            await get().initialize();
            return true;
          }
          return false;
        } catch (error) {
          console.error('❌ SSO login failed:', error);
          return false;
        }
      },
      
      /**
       * Déconnexion
       * Nettoie l'état local du store (le nettoyage localStorage est fait par authService)
       */
      logout: async () => {
        try {
          await authApi.logout();
        } catch (error) {
          console.error('Logout API error:', error);
        } finally {
          // Nettoyer tous les tokens possibles
          localStorage.removeItem('token');
          localStorage.removeItem('auth_token');
          
          // Réinitialiser le store
          set({
            user: null,
            organization: null,
            businessUnit: null,
            isAuthenticated: false,
            loading: false,
            syncStatus: 'idle',
            auditorProfile: undefined,
          });
          // Nettoyer aussi le contexte auditeur
          useAuditorStore.getState().setAuditorProfile(null);
          useAuditorStore.getState().clearActiveCompany();
        }
      },
      
      // Setters
      setUser: (user) => set({ user }),
      setOrganization: (organization) => set({ organization }),
      setBusinessUnit: (businessUnit) => set({ businessUnit }),
      
      /**
       * Vérifie si l'utilisateur a accès au niveau entreprise
       * (pas de businessUnitId assigné = scope company)
       */
      hasCompanyAccess: () => {
        const { user, businessUnit } = get();
        if (!user) return false;
        
        // Les admins et super_admins ont accès company
        if (user.role === 'admin' || user.role === 'super_admin') {
          return true;
        }
        
        // Vérifier le scope du businessUnit
        return businessUnit?.scope === 'company';
      },
      
      /**
       * Retourne le scope de l'utilisateur
       */
      getUserScope: () => {
        const { businessUnit, user } = get();
        if (!user) return null;
        
        // Les admins ont toujours un scope company
        if (user.role === 'admin' || user.role === 'super_admin') {
          return 'company';
        }
        
        return businessUnit?.scope || 'unit';
      },
      
      /**
       * Vérifie si l'utilisateur peut accéder à une unité donnée
       */
      canAccessBusinessUnit: (businessUnitId: string) => {
        const { user, businessUnit } = get();
        if (!user) return false;
        
        // Scope company = accès à tout
        if (get().hasCompanyAccess()) return true;
        
        // Scope unit = uniquement son unité et descendants
        // Note: La vérification des descendants devrait être faite côté backend
        // Ici on vérifie juste si c'est la même unité
        return businessUnit?.id === businessUnitId;
      }
    }),
    {
      name: 'wanzo-auth-storage',
      partialize: (state) => ({
        // Ne persister que l'état d'authentification, pas les données sensibles
        isAuthenticated: state.isAuthenticated,
        syncStatus: state.syncStatus
      })
    }
  )
);

/**
 * Sélecteurs pour usage optimisé
 */
export const selectUser = (state: AuthStore) => state.user;
export const selectOrganization = (state: AuthStore) => state.organization;
export const selectBusinessUnit = (state: AuthStore) => state.businessUnit;
export const selectIsAuthenticated = (state: AuthStore) => state.isAuthenticated;
export const selectAuthLoading = (state: AuthStore) => state.loading;
